import { Badge, Button, LoadingOverlay, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconMicrophone, IconPencil, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FeedItemComments } from 'src/Components/Comments/FeedItemComments';
import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { MEMORY_KIND_COLOR, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { VoiceStoryPlayer } from 'src/Components/Memories/VoiceStoryPlayer';
import { VoiceStoryRecorder } from 'src/Components/Memories/VoiceStoryRecorder';
import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useDeleteMemory, useDeleteMemoryRecording } from 'src/Hooks/Mutations/useMemoryMutations';
import useFetchMemory from 'src/Hooks/Queries/useFetchMemory';
import useFetchSingleFeedItem from 'src/Hooks/Queries/useFetchSingleFeedItem';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './MemoryDetails.module.scss';
import { Props } from './MemoryDetails.types';

/** The memory sheet shown in its view drawer, laid out like TaskDetails. */
const MemoryDetails = ({ memoryId, openRecorderOnMount, onEdit, onDeleted }: Props) => {
  const { t } = useTranslation();
  const { data: memory, isLoading, isError } = useFetchMemory(memoryId);
  // The written memories are the album's comments: same component as Ma communauté.
  const { data: feedItem } = useFetchSingleFeedItem(memoryId);
  // Right after creating a memory without a story, go straight to telling it out loud.
  const [recorderOpened, recorder] = useDisclosure(!!openRecorderOnMount);
  const { mutate: deleteMemory } = useDeleteMemory(memoryId);
  const { mutate: deleteRecording } = useDeleteMemoryRecording(memoryId);

  if (isLoading) return <LoadingOverlay visible />;
  if (isError || !memory) {
    return <Text>{t('memories.detail.error', 'Impossible de retrouver ce souvenir.')}</Text>;
  }

  const dateLabel = formatMemoryDate(
    t,
    memory.takenAt,
    memory.takenAtPrecision,
    memory.takenAtApproximate
  );

  const handleDelete = () =>
    deleteMemory(undefined, {
      onSuccess: () => {
        notifications.show({
          color: 'teal',
          message: t('memories.detail.deleted', 'Souvenir supprimé.'),
        });
        onDeleted();
      },
      onError: () => {
        notifications.show({
          color: 'red',
          message: t(
            'memories.detail.deleteError',
            "Le souvenir n'a pas pu être supprimé. Réessayez dans un instant."
          ),
        });
      },
    });

  const deleteConfirm = {
    title: t('memories.detail.deleteTitle', 'Supprimer ce souvenir ?'),
    message: t(
      'memories.detail.deleteMessage',
      'Il disparaîtra de la mémoire de la maison et de Ma communauté. Les photos restent dans Documents.'
    ),
  };

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: deleteConfirm.title,
      children: <Text size="sm">{deleteConfirm.message}</Text>,
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: handleDelete,
    });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.title}>{memory.title}</div>
          {memory.memoryKind && (
            <div className={styles.badges}>
              <Badge variant="light" size="sm" color={MEMORY_KIND_COLOR[memory.memoryKind]}>
                {memoryKindLabel(t, memory.memoryKind)}
              </Badge>
            </div>
          )}
        </div>
        {memory.canEdit && (
          <div className={styles.headerActions}>
            <RowActions
              actions={[
                {
                  key: 'edit',
                  label: t('common.edit', 'Modifier'),
                  icon: <IconPencil size={18} />,
                  onClick: onEdit,
                },
                {
                  key: 'delete',
                  label: t('common.delete', 'Supprimer'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  confirm: deleteConfirm,
                  onClick: handleDelete,
                },
              ]}
            />
          </div>
        )}
      </div>

      <div className={styles.detailSection}>
        <div className={styles.sectionTitle}>
          {t('memories.detail.informations', 'Informations')}
        </div>
        <KeyValueLine
          propertyKey={t('memories.form.date', 'Date')}
          value={dateLabel ? dateLabel : '-'}
        />
        <KeyValueLine
          propertyKey={t('memories.form.roomsLabel', 'Pièces')}
          value={memory.rooms.length > 0 ? memory.rooms.map(room => room.name).join(', ') : '-'}
        />
        <KeyValueLine
          propertyKey={t('memories.form.placeLabel', 'Lieu précis')}
          value={memory.place ?? '-'}
        />
        <KeyValueLine
          propertyKey={t('memories.detail.peopleLabel', 'Personnes présentes')}
          value={
            memory.people.length > 0
              ? memory.people.map(person => person.displayName).join(', ')
              : '-'
          }
        />
        <KeyValueLine
          propertyKey={t('memories.detail.author', 'Raconté par')}
          value={memory.author.displayName}
        />
      </div>

      {memory.description && (
        <div className={styles.detailSection}>
          <div className={styles.sectionTitle}>{t('common.description', 'Description')}</div>
          <div className={styles.description}>{memory.description}</div>
        </div>
      )}

      {memory.files.length > 0 && (
        <div className={styles.detailSection}>
          <div className={styles.sectionTitle}>{t('memories.form.photos', 'Photos')}</div>
          <div className={styles.gallery}>
            {memory.files.map(file => (
              <a key={file.id} href={file.url} target="_blank" rel="noreferrer">
                <img src={file.previewLargeUrl ?? file.url} alt="" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className={styles.detailSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>{t('memories.detail.stories', 'Les récits')}</div>
          <Button
            variant="outline"
            leftSection={<IconMicrophone size={20} />}
            onClick={recorder.open}
          >
            {t('memories.detail.addStoryLabel', 'Ajouter un récit')}
          </Button>
        </div>
        {memory.recordings.length === 0 ? (
          <div className={styles.empty}>
            {t(
              'memories.detail.noStory',
              'Personne n’a encore raconté ce souvenir à voix haute. Et si c’était vous ?'
            )}
          </div>
        ) : (
          memory.recordings.map(recording => (
            <VoiceStoryPlayer
              key={recording.id}
              recording={recording}
              onDelete={recordingId =>
                deleteRecording(recordingId, {
                  onError: () => {
                    notifications.show({
                      color: 'red',
                      message: t(
                        'memories.recording.deleteError',
                        "Le récit n'a pas pu être retiré. Réessayez dans un instant."
                      ),
                    });
                  },
                })
              }
            />
          ))
        )}
      </div>

      <LinkedEntitiesSection
        entityType={LinkableEntityType.CommunityFeedItem}
        entityId={memory.id}
        entityTitle={memory.title}
      />

      {feedItem && (
        <div className={styles.detailSection}>
          <div className={styles.sectionTitle}>
            {t('memories.detail.comments', 'Ils s’en souviennent aussi')}
          </div>
          <FeedItemComments feedItem={feedItem} />
        </div>
      )}

      {memory.canEdit && (
        <>
          <div className={styles.border} />
          <div className={styles.buttons}>
            <Button onClick={confirmDelete} variant="danger">
              {t('common.delete', 'Supprimer')}
            </Button>
            <Button onClick={onEdit} variant="outline">
              {t('common.edit', 'Modifier')}
            </Button>
          </div>
        </>
      )}

      <VoiceStoryRecorder memoryId={memory.id} opened={recorderOpened} onClose={recorder.close} />
    </div>
  );
};

export default MemoryDetails;
